import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type ShowSuccess = (message: string) => void;

const SuccessSnackbarContext = createContext<ShowSuccess | null>(null);

// Hook must live alongside provider for shared context
// eslint-disable-next-line react-refresh/only-export-components
export function useSuccessSnackbar(): ShowSuccess {
  const show = useContext(SuccessSnackbarContext);
  if (!show) {
    throw new Error('useSuccessSnackbar must be used within SuccessSnackbarProvider');
  }
  return show;
}

export function SuccessSnackbarProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  const showSuccess = useCallback((text: string) => {
    setMessage(text);
  }, []);

  const handleClose = (
    _event?: Event | React.SyntheticEvent,
    reason?: 'timeout' | 'clickaway' | 'escapeKeyDown',
  ) => {
    if (reason === 'clickaway') return;
    setMessage(null);
  };

  const value = useMemo(() => showSuccess, [showSuccess]);

  return (
    <SuccessSnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={message !== null}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        data-testid="success-snackbar"
      >
        <Alert
          onClose={handleClose}
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SuccessSnackbarContext.Provider>
  );
}
